import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Globe,
  Server,
  Cpu,
} from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'
import { toast } from 'sonner'
import type { Project } from '@/types/conversations'

interface LiveHealthSectionProps {
  project: Project
}

interface ServiceHealth {
  status: string
  running_count: number
  desired_count: number
  events: Array<{ at: string; message: string }>
}

interface HealthData {
  web?: ServiceHealth
  worker?: ServiceHealth
  routing?: Record<string, unknown>
  error?: string
}

export function LiveHealthSection({ project }: LiveHealthSectionProps) {
  const { t } = useTranslation('conversations')
  const [health, setHealth] = useState<HealthData | null>(null)
  const [loading, setLoading] = useState(true)
  const [restarting, setRestarting] = useState<string | null>(null)

  const deployTier = project.deploy_tier
  const hasWorker = deployTier === 'full_stack_with_workers'
  const isFrontendOnly = deployTier === 'frontend_only'

  const fetchHealth = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(
        `/projects/${project.id}/production/health`,
        { headers: csrfHeaders() }
      )
      const data = await response.json()
      if (!response.ok) {
        setHealth({ error: data.error || t('live.sre.health.error') })
      } else {
        setHealth(data)
      }
    } catch {
      setHealth({ error: t('live.sre.health.error') })
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded: only used for error fallback
  }, [project.id])

  useEffect(() => {
    fetchHealth()
  }, [fetchHealth])

  const handleRestart = async (serviceType: string) => {
    setRestarting(serviceType)
    try {
      const response = await fetch(
        `/projects/${project.id}/production/restart`,
        {
          method: 'POST',
          headers: csrfHeaders(),
          body: JSON.stringify({ service_type: serviceType }),
        }
      )
      const data = await response.json()
      if (data.success) {
        toast.success(data.message)
        // Refresh health after short delay
        setTimeout(fetchHealth, 3000)
      } else {
        toast.error(data.error || t('live.sre.health.error'))
      }
    } catch {
      toast.error(t('live.sre.health.error'))
    } finally {
      setRestarting(null)
    }
  }

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
        <span className="text-sm text-muted-foreground">{t('live.sre.health.loading')}</span>
      </div>
    )
  }

  if (health?.error) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-sm text-destructive">{health.error}</div>
      </div>
    )
  }

  const services = [
    ...(!isFrontendOnly && health?.web
      ? [{
          key: 'web',
          label: t('live.sre.health.api'),
          icon: Server,
          data: health.web,
        }]
      : []),
    ...(hasWorker && health?.worker
      ? [{
          key: 'worker',
          label: t('live.sre.health.worker'),
          icon: Cpu,
          data: health.worker,
        }]
      : []),
    // Frontend (CDN) is always shown
    {
      key: 'frontend',
      label: t('live.sre.health.frontend'),
      icon: Globe,
      data: null as ServiceHealth | null, // CDN is always up if deployed
    },
  ]

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">{t('live.sre.health.title')}</h3>
        <Button variant="ghost" size="sm" onClick={fetchHealth} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('live.sre.health.refresh')}
        </Button>
      </div>

      <div className="space-y-3">
        {services.map((service) => {
          const isUp = service.key === 'frontend'
            ? project.production_status === 'deployed'
            : service.data && service.data.running_count > 0

          return (
            <div key={service.key} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-md ${
                    isUp ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    <service.icon className={`h-4 w-4 ${
                      isUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{service.label}</span>
                      {service.key === 'frontend' && (
                        <span className="text-xs text-muted-foreground">({t('live.sre.health.cdnActive')})</span>
                      )}
                    </div>
                    {service.data && (
                      <p className="text-xs text-muted-foreground">
                        {service.data.running_count}/{service.data.desired_count} {t('live.sre.health.running').toLowerCase()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {service.key !== 'frontend' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRestart(service.key)}
                      disabled={restarting !== null}
                      className="text-xs"
                    >
                      {restarting === service.key ? (
                        <><RefreshCw className="h-3 w-3 mr-1 animate-spin" />{t('live.sre.health.restarting')}</>
                      ) : (
                        t('live.sre.health.restart')
                      )}
                    </Button>
                  )}

                  <Badge
                    variant={isUp ? 'default' : 'destructive'}
                    className={isUp ? 'bg-green-600' : ''}
                  >
                    {isUp ? (
                      <><CheckCircle2 className="h-3 w-3 mr-1" />{t('live.sre.health.running')}</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" />{t('live.sre.health.down')}</>
                    )}
                  </Badge>
                </div>
              </div>

              {/* Recent events */}
              {service.data?.events && service.data.events.length > 0 && (
                <div className="mt-3 border-t border-border pt-2">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {t('live.sre.health.recentEvents')}
                  </p>
                  <div className="space-y-1">
                    {service.data.events.slice(0, 3).map((event, i) => (
                      <p key={i} className="text-xs text-muted-foreground truncate">
                        <span className="text-foreground/60">
                          {new Date(event.at).toLocaleTimeString()}
                        </span>{' '}
                        {simplifyEventMessage(event.message)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function simplifyEventMessage(message: string): string {
  // Strip ECS-specific jargon for user-friendly display
  return message
    .replace(/\(service .+?\) /g, '')
    .replace(/has reached a steady state\./g, 'is running normally.')
    .replace(/has started \d+ tasks?:/g, 'started new instance.')
    .replace(/registered \d+ targets/g, 'registered new instance')
    .replace(/\bdeployment\b/gi, 'deploy')
}
